import {Popper, PopperProps, useTheme} from '@mui/material'
import {debounce} from 'lodash'
import {memo, RefObject, useCallback, useLayoutEffect, useMemo, useState} from 'react'

export interface GanttChartTooltip {
  tooltipContent: React.ReactNode
  tooltipModifiers?: PopperProps['modifiers']
  tooltipSx?: PopperProps['sx']
}

interface GanttChartItemPopperProps extends GanttChartTooltip {
  anchorElRef: RefObject<HTMLElement | null>
}

const _GanttChartItemTooltip: React.FC<GanttChartItemPopperProps> = ({
  anchorElRef,
  tooltipContent,
  tooltipModifiers,
  tooltipSx
}) => {
  const theme = useTheme()

  const [tooltipAnchorEl, setTooltipAnchorEl] = useState<HTMLElement | null>(null)

  const debouncedOpenTooltip = useMemo(
    () => debounce(() => setTooltipAnchorEl(anchorElRef.current), 300),
    [anchorElRef]
  )

  const onMouseOver = useCallback(() => {
    debouncedOpenTooltip()
  }, [debouncedOpenTooltip])

  const onMouseOut = useCallback(() => {
    debouncedOpenTooltip.cancel()
    setTooltipAnchorEl(null)
  }, [debouncedOpenTooltip])

  useLayoutEffect(() => {
    const anchorEl = anchorElRef.current

    if (!anchorEl) return

    anchorEl.addEventListener('mouseover', onMouseOver)
    anchorEl.addEventListener('mouseout', onMouseOut)

    return () => {
      anchorEl.removeEventListener('mouseover', onMouseOver)
      anchorEl.removeEventListener('mouseout', onMouseOut)
    }
  }, [anchorElRef, onMouseOver, onMouseOut])

  return (
    <Popper
      open={!!tooltipAnchorEl}
      anchorEl={tooltipAnchorEl}
      placement="bottom"
      sx={{
        zIndex: 2,
        ...tooltipSx
      }}
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, parseInt(theme.spacing(1), 10)]
          }
        },
        {name: 'flip', enabled: true},
        {name: 'hide', enabled: true},
        {name: 'preventOverflow', enabled: true, options: {rootBoundary: 'viewport'}},
        ...(tooltipModifiers ? tooltipModifiers : [])
      ]}
    >
      {tooltipContent}
    </Popper>
  )
}

export const GanttChartItemTooltip = memo(_GanttChartItemTooltip)
